import type { Nullable } from '@mosaik/core';
import { ArgumentException, Exception } from '@mosaik/core';

/**
 * `ColorSet` -  Enum which describes all possible color set options.
 * @public
 * @enum
 */
export enum ColorSet {
  /**
   * `default` - The default color set option.
   * @default
   */
  Default = 'default',
  /**
   * `primary` - The primary color set option.
   */
  Primary = 'primary',
  /**
   * `secondary` - The secondary color set option.
   */
  Secondary = 'secondary',
  /**
   * `success` - The success color set option.
   */
  Success = 'success',
  /**
   * `danger` - The danger color set option.
   */
  Danger = 'danger',
  /**
   * `warning` - The warning color set option.
   */
  Warning = 'warning',
  /**
   * `info` - The info color set option.
   */
  Info = 'info',
  /**
   * `contrast` - The contrast color set option.
   */
  Contrast = 'contrast'
}

/**
 * @public
 */
// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace ColorSet {
  /**
   * Parses a `value` and returns an instance of the {@link (ColorSet:enum)} enum.
   * @public
   * @param value - The `value` to parse.
   * @returns A `ColorSet` if the given `value` is a known member of the {@link (ColorSet:enum)} enum.
   */
  export function parse(value: string | ColorSet): ColorSet {
    if (typeof value === 'string' && value === '') {
      throw new ArgumentException(`The value could not be empty.`, 'value');
    }
    if (value === undefined) {
      throw new ArgumentException(`The value could not be null or undefined.`, 'value');
    }

    const parsedValue = tryParse(value);

    if (parsedValue !== null && parsedValue !== undefined) {
      return parsedValue;
    }

    throw new Exception(`Invalid color set value: '${String(value)}'.`);
  }

  /**
   * Try parses a value and returns an instance of the {@link (ColorSet:enum)} enum, `null` or `undefined`.
   * @public
   * @param value - The value to parse.
   * @returns A `ColorSet` if the given `value` is a known member of the {@link (ColorSet:enum)} enum.
   * If the given `value` is not an known `value` it will returns `undefined`.
   * If the given `value` is `null` it will returns `null`.
   */
  export function tryParse(value: string | ColorSet | null): Nullable<ColorSet> {
    if (value !== null) {
      switch (value) {
        case 'default':
          // tslint:disable-next-line: no-unnecessary-qualifier
          return ColorSet.Default;
        case 'primary':
          // tslint:disable-next-line: no-unnecessary-qualifier
          return ColorSet.Primary;
        case 'secondary':
          // tslint:disable-next-line: no-unnecessary-qualifier
          return ColorSet.Secondary;
        case 'success':
          // tslint:disable-next-line: no-unnecessary-qualifier
          return ColorSet.Success;
        case 'danger':
          // tslint:disable-next-line: no-unnecessary-qualifier
          return ColorSet.Danger;
        case 'warning':
          // tslint:disable-next-line: no-unnecessary-qualifier
          return ColorSet.Warning;
        case 'info':
          // tslint:disable-next-line: no-unnecessary-qualifier
          return ColorSet.Info;
        case 'contrast':
          // tslint:disable-next-line: no-unnecessary-qualifier
          return ColorSet.Contrast;
        default:
          return undefined;
      }
    }
    return null;
  }
}
